import { useMatch } from '@tanstack/react-location'
import { useEffect, useState } from 'react'
import { Avatar } from 'src/core/ds/avatar'
import { AVATAR_SIZES } from 'src/core/ds/avatar/consts'
import { Button } from 'src/core/ds/button'
import { ButtonSizes, ButtonVariants } from 'src/core/ds/button/types'
import { IconNames } from 'src/core/ds/icon'
import { Input } from 'src/core/ds/input'
import { useDebounce } from 'src/hooks/useDebounce'
import { pushNotification } from 'src/services/notifications/notificationService'
import { useUIStore } from 'src/services/store/UIStore'
import { api } from '../teams/api'
import { useAddOrRemoveTeamMember } from '../teams/query'
import styles from './index.module.scss'

export function AddTeamMembersModal() {
  const updateUIStore = useUIStore((state) => state.updateUIStore)
  const {
    params: { teamId },
  } = useMatch()

  const [name, setName] = useState('')
  const [team, setTeam] = useState<any>([])
  const debouncedValue = useDebounce(name, 500)
  const [searchResults, setSearchResults] = useState<any>(null)

  const onSuccess = () => {
    pushNotification({
      type: 'success',
      title: 'Added new members',
    })
    updateUIStore('isAddTeamMemberModalOpen', false)
  }

  const { mutate, isLoading } = useAddOrRemoveTeamMember(onSuccess)

  const addToTeam = (member) => {
    const isExistingMember = team.some((memberL) => memberL?.id?.toString() === member?.id?.toString())

    // eslint-disable-next-line
    // @ts-ignore
    if (!isExistingMember) {
      setTeam((prevState) => [...prevState, member])
    }
  }

  const removeFromTeam = (id) => {
    // eslint-disable-next-line
    // @ts-ignore
    const filteredMembers = team.filter((member) => member?.id?.toString() !== id)
    setTeam(filteredMembers)
  }

  useEffect(() => {
    if (debouncedValue.length < 3) {
      setSearchResults(null)

      return
    }

    api
      .searchUsers({ username: debouncedValue })
      .then((res: any) => {
        const [error, response] = res

        if (error) {
          console.log('error', error)
        }

        if (response?.items) {
          setSearchResults(response.items)
        }

        if (response?.length === 0) {
          setSearchResults([])
        }
      })
      .catch((err) => {
        console.log('error', err)
      })
  }, [debouncedValue])

  const handleChange = (event) => {
    const { value } = event.target
    setName(value)
  }

  const onAddTeamMembers = async () => {
    await mutate({
      // eslint-disable-next-line
      // @ts-ignore
      id: teamId,
      details: {
        type: 'member',
        add: team.map((member) => member.id.toString()),
      },
    })
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.splitDetails}>
        <div className={styles.detailsForm}>
          <div>
            <div className={styles.formControl}>
              <Input
                id="title"
                value={name}
                onChange={handleChange}
                type="text"
                label="Search for a team member"
                // error={isNameInvalid ? 'error' : undefined}
                placeholder="Start typing username"
              />
              {searchResults && (
                <div className={styles.foundList}>
                  {searchResults?.map((item) => (
                    <div key={item?.id?.toString()} className={`${styles.listItem}`}>
                      <div className={styles.listItemAuthor}>
                        <Avatar
                          size={AVATAR_SIZES.xs}
                          url={
                            item?.avatar_url ||
                            'https://zentask.ai/static/media/zentask-new-logo.00471ab3f7b393c5d6a7.png'
                          }
                        />
                        <span>{item?.username || item?.full_name}</span>
                      </div>
                      <div className={styles.listItemAction}>
                        <Button
                          onClick={() => addToTeam(item)}
                          icon={IconNames.plus}
                          type="button"
                          label="Add"
                          size={ButtonSizes.xs}
                        />
                      </div>
                    </div>
                  ))}
                  {searchResults?.length === 0 && <div className={styles.notFound}>Nothing found.</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {team.length > 0 && (
        <div className={styles.detailsForm}>
          <h2 className={styles.teamTitle}>Members to add</h2>
          <div className={styles.members}>
            {team?.map((item) => (
              <div key={item?.id?.toString()} className={`${styles.listItem}`}>
                <div className={styles.listItemAuthor}>
                  <Avatar
                    size={AVATAR_SIZES.xs}
                    url={
                      item?.avatar_url || 'https://zentask.ai/static/media/zentask-new-logo.00471ab3f7b393c5d6a7.png'
                    }
                  />
                  <span>{item?.username || item?.full_name}</span>
                </div>
                <div className={styles.listItemAction}>
                  <Button
                    icon={IconNames.delete}
                    variant={ButtonVariants.unstyled}
                    type="button"
                    onClick={() => removeFromTeam(item?.id?.toString())}
                    label=""
                    iconColor="var(--color-secondary)"
                    size={ButtonSizes.xs}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className={styles.actionFooter}>
        <Button
          isLoading={isLoading}
          disabled={isLoading || team.length < 1}
          onClick={onAddTeamMembers}
          type="button"
          label="Save"
        />
      </div>
    </div>
  )
}
